/**
 * ProgramHeader.jsx
 *
 * Displays program name, overall progress bar, and credit statistics
 * at the top of the Curriculum Map view.
 * Uses HSG color palette to match StudyOverview styling.
 */

import PropTypes from "prop-types";
import { QuestionMarkCircleIcon } from "@heroicons/react/outline";
import { Tooltip as ReactTooltip } from "react-tooltip";
import CurriculumMapBetaNotice from "./CurriculumMapBetaNotice";

const ProgramHeader = ({ program, onHelpClick }) => {
  if (!program) return null;

  const {
    name,
    totalRequired,
    totalEarned,
    totalPlanned,
    completionPercentage,
    estimatedCompletion,
  } = program;

  // Progress bar segments
  const earnedPercent = Math.min((totalEarned / totalRequired) * 100, 100);
  const plannedPercent = Math.min(
    ((totalEarned + totalPlanned) / totalRequired) * 100 - earnedPercent,
    100 - earnedPercent
  );

  return (
    <div className="space-y-3">
      {/* Title - matches StudyOverview header style */}
      <div className="py-2 pl-2 pr-3 text-xl font-bold bg-gray-100 rounded flex items-center justify-between">
        <span className="flex items-center gap-2 min-w-0">
          <span className="truncate">{name}</span>
          <span
            className="inline-flex flex-shrink-0 items-center rounded-full bg-hsg-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-hsg-700 ring-1 ring-hsg-700/20 cursor-help"
            data-tooltip-id="curriculum-map-beta"
          >
            Beta
          </span>
        </span>
        <button
          className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 flex-shrink-0 ml-2 transition-colors"
          data-tooltip-id="curriculum-map-help"
          onClick={onHelpClick}
        >
          <QuestionMarkCircleIcon className="h-4 w-4" />
          How this works
        </button>
      </div>

      {/* One-time Beta welcome callout (dismissal persisted in localStorage) */}
      <CurriculumMapBetaNotice />

      {/* Progress section */}
      <div className="px-2">
        {/* Credits summary */}
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm text-gray-600">
            <span className="font-semibold text-green-700">{totalEarned}</span>
            {totalPlanned > 0 && (
              <>
                <span className="text-gray-400"> + </span>
                <span className="font-semibold text-gray-500">{totalPlanned}</span>
                <span className="text-gray-400"> planned</span>
              </>
            )}
            <span className="text-gray-400"> / </span>
            <span className="font-semibold">{totalRequired}</span>
            <span className="text-gray-500"> ECTS</span>
          </div>
          <span className="text-sm font-medium text-gray-500">{completionPercentage}%</span>
        </div>

        {/* Progress bar - simplified without milestone markers */}
        <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden flex">
          {/* Earned (completed) segment */}
          <div
            className="h-full bg-green-600 transition-all duration-500"
            style={{ width: `${earnedPercent}%` }}
            title={`${totalEarned} ECTS completed`}
          />
          {/* Planned segment */}
          {totalPlanned > 0 && (
            <div
              className="h-full bg-gray-400 transition-all duration-500"
              style={{ width: `${plannedPercent}%` }}
              title={`${totalPlanned} ECTS planned`}
            />
          )}
        </div>

        {/* Completion status */}
        {estimatedCompletion === "Completed" && (
          <div className="text-xs text-green-700 font-medium mt-2">
            All requirements completed
          </div>
        )}
      </div>

      <ReactTooltip
        id="curriculum-map-help"
        place="bottom"
        content="Click for the Tutorial"
        style={{
          backgroundColor: "#f9fafb",
          color: "#111827",
          border: "1px solid #d1d5db",
          borderRadius: "0.375rem",
          fontSize: "0.8125rem",
          padding: "6px 10px",
          zIndex: 50,
        }}
      />

      <ReactTooltip
        id="curriculum-map-beta"
        place="bottom"
        content="This feature is in Beta and still being refined. Your feedback is welcome!"
        style={{
          backgroundColor: "#f9fafb",
          color: "#111827",
          border: "1px solid #d1d5db",
          borderRadius: "0.375rem",
          fontSize: "0.8125rem",
          padding: "6px 10px",
          maxWidth: "240px",
          zIndex: 50,
        }}
      />
    </div>
  );
};

ProgramHeader.propTypes = {
  program: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    totalRequired: PropTypes.number.isRequired,
    totalEarned: PropTypes.number.isRequired,
    totalPlanned: PropTypes.number.isRequired,
    completionPercentage: PropTypes.number.isRequired,
    estimatedCompletion: PropTypes.string,
  }),
  onHelpClick: PropTypes.func,
};

export default ProgramHeader;
